import React, { useState, useEffect } from 'react';
import SepaStatementTab from './SepaStatementTab';
import SepaChargebacksTab from './SepaChargebacksTab';
import SepaMandateImportTab from './SepaMandateImportTab';
import SepaTransfersTab from './SepaTransfersTab';
import FintsSubmitDialog from './FintsSubmitDialog';
import FintsConnectionsDialog from './FintsConnectionsDialog';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { PlzOrtBridge } from '@/components/address/PlzOrtBridge';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Download,
  FileText,
  Plus,
  Settings,
  Play,
  Eye,
  Loader2,
  Trash2,
  RefreshCw,
  ArrowLeftRight,
  ShieldCheck,
  FileCode2,
  Landmark,
} from 'lucide-react';
import { toast } from 'sonner';
import { lookupIbanInfo, normalizeIban } from '@/lib/iban';
import ImikelImport from './ImikelImport';
import Layout from '@/components/layout/Layout';
import DateInput from '@/components/ui/date-input';
import { apiV1, tenantHeaders } from '@/lib/apiBase';
import { formatDate as formatDateDe } from '@/lib/utils';
import { fruehesterBankarbeitstag } from '@/lib/bankDay';

const nextDebitDate = (day) => {
  const minDate = new Date();
  minDate.setDate(minDate.getDate() + 3);
  const d = new Date(minDate.getFullYear(), minDate.getMonth(), day);
  if (d < minDate) d.setMonth(d.getMonth() + 1);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const monatZumTermin = (iso) => (iso && iso.length >= 7 ? `${iso.slice(0, 7)}-01` : '');

const monatsname = (iso) => {
  if (!iso) return '';
  const tag = new Date(`${String(iso).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(tag.getTime())) return '';
  return tag.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
};

const LEERES_KONTO = {
  name: '', iban: '', bic: '', creditor_id: '', address: '', city: '', postal_code: '', country: 'DE',
};

function alsKonten(daten) {
  if (Array.isArray(daten)) return daten.filter((k) => k && k.iban);
  if (daten && daten.iban) return [daten];
  return [];
}

function kontoFuerEinzugstag(konten, tag) {
  const liste = alsKonten(konten);
  return liste.find((k) => k.debit_day === tag) || liste.find((k) => k.debit_day == null) || null;
}

function formularAus(konto, vorlage) {
  const basis = konto || vorlage || LEERES_KONTO;
  return {
    name: basis.name || '',
    iban: konto ? (konto.iban || '') : '',
    bic: konto ? (konto.bic || '') : '',
    creditor_id: basis.creditor_id || '',
    address: basis.address || '',
    city: basis.city || '',
    postal_code: basis.postal_code || '',
    country: basis.country || 'DE',
  };
}

const SepaPage = () => {
  const [activeTab, setActiveTab] = useState('runs');
  const [chargebackRefresh, setChargebackRefresh] = useState(0);
  const [imikelTenant, setImikelTenant] = useState('MA-GRE');
  const [runs, setRuns] = useState([]);
  const [konten, setKonten] = useState([]);
  const [configTag, setConfigTag] = useState(1);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [showRunDetailDialog, setShowRunDetailDialog] = useState(false);
  const [showCompareDialog, setShowCompareDialog] = useState(false);
  const [showCheckDialog, setShowCheckDialog] = useState(false);
  const [checkResult, setCheckResult] = useState(null);
  const [checkingRun, setCheckingRun] = useState(null);
  const [checkLaufId, setCheckLaufId] = useState(null);
  const [befundeOffen, setBefundeOffen] = useState({});
  const [showBanks, setShowBanks] = useState(false);
  const [gewaehltePosten, setGewaehltePosten] = useState([]);
  const [formatInfo, setFormatInfo] = useState(null);
  const [fintsTarget, setFintsTarget] = useState(null);
  
  // Form states
  const [configForm, setConfigForm] = useState({
    name: '',
    iban: '',
    bic: '',
    creditor_id: '',
    address: '',
    city: '',
    postal_code: ''
  });
  
  const [previewForm, setPreviewForm] = useState(() => {
    const execution_date = nextDebitDate(1);
    return {
      execution_date,
      billing_month: monatZumTermin(execution_date),
      debit_day: 1,
      execution_mode: 'scheduled',
    };
  });
  
  // Nächstes Ausführungsdatum (01. oder 15.) mit mind. 3 Tagen Vorlauf berechnen
  const suggestExecutionDate = (day) => {
    return nextDebitDate(day);
  };

  const handleDebitDayChange = (value) => {
    const day = Number(value);
    setPreview(null);
    setPreviewForm((form) => {
      if (form.execution_mode && form.execution_mode !== 'scheduled') {
        return { ...form, debit_day: day };
      }
      const suggested = suggestExecutionDate(day);
      toast.info(`Ausführungsdatum automatisch auf den ${formatDate(suggested)} gesetzt`);
      return {
        ...form,
        debit_day: day,
        execution_date: suggested,
        billing_month: monatZumTermin(suggested),
        execution_mode: 'scheduled',
      };
    });
  };

  const setzeAusfuehrung = (mode) => {
    setPreview(null);
    setPreviewForm((form) => {
      if (mode === 'immediate') {
        const execution_date = fruehesterBankarbeitstag();
        return {
          ...form,
          execution_mode: 'immediate',
          execution_date,
          billing_month: monatZumTermin(execution_date),
        };
      }
      if (mode === 'custom') return { ...form, execution_mode: 'custom' };
      const execution_date = suggestExecutionDate(form.debit_day);
      return {
        ...form,
        execution_mode: 'scheduled',
        execution_date,
        billing_month: monatZumTermin(execution_date),
      };
    });
  };

  const executionDayMismatch = (() => {
    if (previewForm.execution_mode && previewForm.execution_mode !== 'scheduled') return false;
    if (!previewForm.debit_day || !previewForm.execution_date) return false;
    const d = new Date(previewForm.execution_date + 'T00:00:00');
    return d.getDate() !== previewForm.debit_day;
  })();

  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [selectedRun, setSelectedRun] = useState(null);
  const [compareRuns, setCompareRuns] = useState({ run1: '', run2: '' });
  const [comparison, setComparison] = useState(null);

  const authHeaders = () => tenantHeaders();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch creditor config
      const configRes = await fetch(`${apiV1()}/sepa/creditor-configs`, {
        credentials: 'include',
        headers: authHeaders()
      });
      const configData = await configRes.json();
      const liste = alsKonten(configData);
      setKonten(liste);
      setConfigForm(formularAus(kontoFuerEinzugstag(liste, 1)));
      
      // Fetch runs
      const runsRes = await fetch(`${apiV1()}/sepa/runs`, {
        credentials: 'include',
        headers: authHeaders()
      });
      const runsData = await runsRes.json();
      setRuns(runsData || []);

      // Nachrichtenversion: pain.008.001.02 wird am 14.11.2026 abgeschaltet.
      const formatRes = await fetch(`${apiV1()}/sepa/format`, {
        credentials: 'include',
        headers: authHeaders()
      });
      if (formatRes.ok) setFormatInfo(await formatRes.json());
    } catch (error) {
      console.error('Fehler beim Laden:', error);
      toast.error('Fehler beim Laden der SEPA-Daten');
    } finally {
      setLoading(false);
    }
  };

  const setSepaFormat = async (einstellung) => {
    try {
      const res = await fetch(`${apiV1()}/sepa/format`, {
        method: 'POST',
        credentials: 'include',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ einstellung })
      });
      if (!res.ok) throw new Error(await res.text());
      toast.success('Nachrichtenversion gespeichert');
      fetchData();
    } catch (error) {
      toast.error('Nachrichtenversion konnte nicht gespeichert werden');
    }
  };

  const oeffneKonten = (tag = 1) => {
    setConfigTag(tag);
    const eigen = konten.find((k) => k.debit_day === tag);
    setConfigForm(formularAus(eigen || (tag === 1 ? kontoFuerEinzugstag(konten, 1) : null), kontoFuerEinzugstag(konten, 1)));
    setShowConfigDialog(true);
  };

  const saveCreditorConfig = async () => {
    try {
      const res = await fetch(`${apiV1()}/sepa/creditor-config`, {
        credentials: 'include',
        method: 'POST',
        headers: {
          ...authHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ...configForm, debit_day: configTag })
      });
      
      if (res.ok) {
        setShowConfigDialog(false);
        toast.success('Gläubiger-Konfiguration gespeichert');
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Fehler beim Speichern');
      }
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  const loadPreview = async () => {
    if (executionDayMismatch || previewLoading) {
      if (executionDayMismatch) toast.error('Ausführungsdatum und Einzugstag müssen übereinstimmen');
      return;
    }
    setPreviewLoading(true);
    try {
      const res = await fetch(`${apiV1()}/sepa/runs/preview`, {
        credentials: 'include',
        method: 'POST',
        headers: {
          ...authHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(previewForm)
      });
      
      if (res.ok) {
        const data = await res.json();
        setPreview(data);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Fehler bei der Vorschau');
      }
    } catch (error) {
      toast.error('Fehler bei der Vorschau');
    } finally {
      setPreviewLoading(false);
    }
  };

  const createRun = async () => {
    if (executionDayMismatch) {
      toast.error('Ausführungsdatum und Einzugstag müssen übereinstimmen');
      return;
    }
    try {
      const res = await fetch(`${apiV1()}/sepa/runs`, {
        credentials: 'include',
        method: 'POST',
        headers: {
          ...authHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(previewForm)
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`SEPA-Lauf #${data.run_number} erstellt mit ${data.transaction_count} Transaktionen`);
        setShowPreviewDialog(false);
        setPreview(null);
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Fehler beim Erstellen');
      }
    } catch (error) {
      toast.error('Fehler beim Erstellen');
    }
  };

  // Prüft, ohne zu exportieren: der Lauf bleibt im Entwurf, es wird keine Datei
  // abgelegt. Deshalb auch für bereits exportierte Läufe sinnvoll — dort zeigt
  // sie, was in der eingereichten Datei steckte.
  const checkRun = async (runId) => {
    setCheckLaufId(runId);
    setCheckingRun(runId);
    setCheckResult(null);
    setBefundeOffen({});
    setShowCheckDialog(true);
    try {
      const res = await fetch(`${apiV1()}/sepa/runs/${runId}/check`, {
        credentials: 'include',
        headers: authHeaders(),
      });
      if (!res.ok) {
        const fehler = await res.json().catch(() => ({}));
        toast.error(fehler.detail || 'Prüfung fehlgeschlagen');
        setShowCheckDialog(false);
        return;
      }
      setCheckResult(await res.json());
    } catch (error) {
      toast.error('Prüfung fehlgeschlagen');
      setShowCheckDialog(false);
    } finally {
      setCheckingRun(null);
    }
  };

  const exportRun = async (runId) => {
    const run = runs.find((item) => item.id === runId);
    if (run && !window.confirm(
      `Lauf #${run.run_number} exportieren?\nEinzugstag: ${String(run.debit_day).padStart(2, '0')}.\nAusführung: ${formatDate(run.execution_date)}`
    )) return;
    try {
      const res = await fetch(`${apiV1()}/sepa/runs/${runId}/export`, {
        credentials: 'include',
        method: 'POST',
        headers: authHeaders()
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Fehler beim Export');
      }
    } catch (error) {
      toast.error('Fehler beim Export');
    }
  };

  const downloadRun = async (runId, filename) => {
    try {
      const res = await fetch(`${apiV1()}/sepa/runs/${runId}/download`, {
        credentials: 'include',
        headers: authHeaders()
      });
      
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename || 'sepa_export.xml';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } else {
        toast.error('Fehler beim Download');
      }
    } catch (error) {
      toast.error('Fehler beim Download');
    }
  };

  const markRunStatus = async (runId, action) => {
    try {
      const res = await fetch(`${apiV1()}/sepa/runs/${runId}/${action}`, {
        credentials: 'include',
        method: 'POST',
        headers: authHeaders()
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Fehler');
      }
    } catch (error) {
      toast.error('Fehler');
    }
  };

  const deleteRun = async (run) => {
    if (!window.confirm(
      `Lastschriftlauf #${run.run_number} löschen? Die Datei wird entfernt. Die Rechnungen bleiben offen.`,
    )) return;

    try {
      const res = await fetch(`${apiV1()}/sepa/runs/${run.id}`, {
        credentials: 'include',
        method: 'DELETE',
        headers: authHeaders()
      });

      if (res.ok) {
        toast.success('Lastschriftlauf gelöscht');
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Löschen fehlgeschlagen');
      }
    } catch (error) {
      toast.error('Löschen fehlgeschlagen');
    }
  };

  const loadRunDetail = async (runId) => {
    try {
      const res = await fetch(`${apiV1()}/sepa/runs/${runId}`, {
        credentials: 'include',
        headers: authHeaders()
      });
      
      if (res.ok) {
        const data = await res.json();
        setSelectedRun(data);
        setGewaehltePosten([]);
        setShowRunDetailDialog(true);
      }
    } catch (error) {
      toast.error('Fehler beim Laden');
    }
  };

  const speichereTermin = async (runId, wert, mode) => {
    const tag = wert ? new Date(`${wert}T00:00:00`).getDate() : null;
    const executionMode = mode || (tag === selectedRun?.debit_day ? 'scheduled' : 'custom');
    setSelectedRun((aktuell) => (
      aktuell ? {
        ...aktuell,
        execution_date: wert,
        billing_month: monatZumTermin(wert) || aktuell.billing_month,
        execution_mode: executionMode,
      } : aktuell
    ));
    if (!/^\d{4}-\d{2}-\d{2}$/.test(wert || '')) return;
    try {
      const res = await fetch(`${apiV1()}/sepa/runs/${runId}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ execution_date: wert, execution_mode: executionMode }),
      });
      if (!res.ok) {
        const fehler = await res.json().catch(() => ({}));
        toast.error(fehler.detail || 'Der Termin konnte nicht gespeichert werden');
        return;
      }
      toast.success('Ausführungsdatum gespeichert');
      fetchData();
    } catch (error) {
      toast.error('Der Termin konnte nicht gespeichert werden');
    }
  };

  const oeffneBefund = (ziel) => {
    if (!ziel?.art) return;
    if (ziel.art === 'glaeubiger') {
      setShowCheckDialog(false);
      oeffneKonten(1);
      return;
    }
    if (ziel.art === 'termin' && checkLaufId) {
      setShowCheckDialog(false);
      loadRunDetail(checkLaufId);
      return;
    }
    if (ziel.art === 'zahler' && ziel.id) {
      window.location.assign(`/payer-accounts?guardian=${encodeURIComponent(ziel.id)}`);
    }
  };

  const togglePosten = (id) => {
    setGewaehltePosten((aktuell) => (
      aktuell.includes(id) ? aktuell.filter((x) => x !== id) : [...aktuell, id]
    ));
  };

  const compareSepaRuns = async () => {
    if (!compareRuns.run1 || !compareRuns.run2) {
      toast.error('Bitte beide Läufe auswählen');
      return;
    }
    
    try {
      const res = await fetch(`${apiV1()}/sepa/runs/compare/${compareRuns.run1}/${compareRuns.run2}`, {
        credentials: 'include',
        headers: authHeaders()
      });
      
      if (res.ok) {
        const data = await res.json();
        setComparison(data);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Fehler beim Vergleich');
      }
    } catch (error) {
      toast.error('Fehler beim Vergleich');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: { variant: 'outline', label: 'Entwurf' },
      exported: { variant: 'default', label: 'Exportiert', className: 'bg-blue-500' },
      submitted: { variant: 'default', label: 'Eingereicht', className: 'bg-yellow-500' },
      completed: { variant: 'default', label: 'Abgeschlossen', className: 'bg-green-500' },
      cancelled: { variant: 'destructive', label: 'Storniert' }
    };
    const s = styles[status] || { variant: 'outline', label: status };
    return <Badge variant={s.variant} className={s.className}>{s.label}</Badge>;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return formatDateDe(dateStr) || '-';
  };

  return (
    <Layout>
    <div className="p-6 space-y-6" data-testid="sepa-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Zahlungsverkehr</h1>
          <p className="text-gray-500">Lastschriften und Überweisungen, direkt an die Bank oder als XML</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setShowBanks(true)}
            data-testid="fints-connections-btn"
          >
            <Landmark className="w-4 h-4 mr-2" />
            Bankverbindungen
          </Button>
          <Button
            variant="outline"
            onClick={() => oeffneKonten(1)}
            data-testid="config-btn"
          >
            <Settings className="w-4 h-4 mr-2" />
            Gläubiger-Einstellungen
          </Button>
          <Button
            onClick={() => {
              setPreview(null);
              setShowPreviewDialog(true);
            }}
            disabled={konten.length === 0}
            data-testid="new-run-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Neuer Lastschriftlauf
          </Button>
        </div>
      </div>

      {/* Creditor Info Banner */}
      {konten.length > 0 ? (
        <Card className="bg-green-50 border-green-200" data-testid="sepa-konten">
          <CardContent className="p-4 space-y-3">
            {[1, 15].map((tag) => {
              const konto = kontoFuerEinzugstag(konten, tag);
              const eigen = konten.some((k) => k.debit_day === tag);
              return (
                <div key={tag} className="flex items-start gap-4" data-testid={`sepa-konto-${tag}`}>
                  <CheckCircle2 className={`w-6 h-6 shrink-0 ${konto ? 'text-green-600' : 'text-amber-600'}`} />
                  <div>
                    <p className="font-medium">
                      Abbuchung zum {String(tag).padStart(2, '0')}.
                      {konto?.bank_label ? ` · ${konto.bank_label}` : ''}
                      {konto ? ` · ${konto.name}` : ' · noch kein Konto'}
                    </p>
                    {konto && (
                      <p className="text-sm text-gray-600">
                        IBAN: {konto.iban} | Gläubiger-ID: {konto.creditor_id}
                      </p>
                    )}
                    {!eigen && konto && tag === 15 && (
                      <p className="text-sm text-amber-800 mt-1">
                        Dasselbe Konto wie zum 01. Für den 15. das Volksbank-Konto hinterlegen.
                      </p>
                    )}
                    {!konto && (
                      <p className="text-sm text-amber-800 mt-1">
                        Für diesen Einzugstag ist noch kein Konto hinterlegt.
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-yellow-50 border-yellow-200">
          <CardContent className="p-4 flex items-center gap-4">
            <AlertCircle className="w-6 h-6 text-yellow-600" />
            <div>
              <p className="font-medium">Keine Gläubiger-Konfiguration</p>
              <p className="text-sm text-gray-600">
                Bitte richten Sie zuerst die Gläubiger-Daten ein, bevor Sie SEPA-Lastschriften erstellen.
              </p>
            </div>
            <Button variant="outline" onClick={() => oeffneKonten(1)}>
              Jetzt einrichten
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Nachrichtenversion: die Institute weisen pain.008.001.02 ab dem 14.11.2026 ab. */}
      {formatInfo && (
        <Card
          data-testid="sepa-format-banner"
          className={formatInfo.umgestellt ? 'bg-gray-50 border-gray-200' : 'bg-blue-50 border-blue-200'}
        >
          <CardContent className="p-4 flex flex-wrap items-center gap-4">
            <FileCode2 className={`w-6 h-6 ${formatInfo.umgestellt ? 'text-gray-500' : 'text-blue-600'}`} />
            <div className="flex-1 min-w-[16rem]">
              <p className="font-medium" data-testid="sepa-format-aktiv">
                Dateiformat: {formatInfo.aktives_format}
              </p>
              <p className="text-sm text-gray-600">{formatInfo.begruendung}</p>
            </div>
            {!formatInfo.umgestellt && (
              <Button
                variant="outline"
                data-testid="sepa-format-jetzt-umstellen"
                onClick={() => setSepaFormat('pain.008.001.08')}
              >
                Jetzt auf pain.008.001.08 umstellen
              </Button>
            )}
            {formatInfo.umgestellt && !formatInfo.automatisch && (
              <Button
                variant="ghost"
                data-testid="sepa-format-zurueck-auf-automatik"
                onClick={() => setSepaFormat('auto')}
              >
                Zurück auf automatisch
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="runs">Lastschriftläufe ({runs.length})</TabsTrigger>
          <TabsTrigger value="transfers" data-testid="tab-transfers">Überweisungen</TabsTrigger>
          <TabsTrigger value="statement" data-testid="tab-statement">Kontoauszug-Import</TabsTrigger>
          <TabsTrigger value="chargebacks" data-testid="tab-chargebacks">Rückbuchungen</TabsTrigger>
          <TabsTrigger value="mandate-import" data-testid="tab-mandate-import">Mandats-Import</TabsTrigger>
          <TabsTrigger value="compare">Vergleich</TabsTrigger>
          <TabsTrigger value="imikel" data-testid="tab-imikel">iMikel-Import</TabsTrigger>
        </TabsList>

        <TabsContent value="transfers" className="mt-4">
          <SepaTransfersTab creditorConfigured={konten.length > 0} />
        </TabsContent>

        <TabsContent value="statement" className="mt-4">
          <SepaStatementTab onImported={() => setChargebackRefresh(k => k + 1)} />
        </TabsContent>

        <TabsContent value="chargebacks" className="mt-4">
          <SepaChargebacksTab refreshKey={chargebackRefresh} />
        </TabsContent>

        <TabsContent value="mandate-import" className="mt-4">
          <SepaMandateImportTab />
        </TabsContent>

        <TabsContent value="runs" className="mt-4">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nr.</TableHead>
                    <TableHead>Ausführung</TableHead>
                    <TableHead>Einzugstag</TableHead>
                    <TableHead>Monat</TableHead>
                    <TableHead>Posten</TableHead>
                    <TableHead>Betrag</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Erstellt</TableHead>
                    <TableHead>Aktionen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {runs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-8 text-gray-500">
                        Noch keine Lastschriftläufe vorhanden
                      </TableCell>
                    </TableRow>
                  ) : (
                    runs.map((run) => (
                      <TableRow key={run.id} data-testid={`sepa-run-${run.run_number}`}>
                        <TableCell className="font-medium">#{run.run_number}</TableCell>
                        <TableCell>{formatDate(run.execution_date)}</TableCell>
                        <TableCell className="font-medium">{String(run.debit_day).padStart(2, '0')}.</TableCell>
                        <TableCell>{formatDate(run.billing_month)}</TableCell>
                        <TableCell>{run.transaction_count}</TableCell>
                        <TableCell className="font-medium">{formatCurrency(run.total_amount)}</TableCell>
                        <TableCell>{getStatusBadge(run.status)}</TableCell>
                        <TableCell>{formatDate(run.created_at)}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => loadRunDetail(run.id)}
                              title="Details anzeigen"
                              data-testid={`sepa-detail-${run.run_number}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>

                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => checkRun(run.id)}
                              disabled={checkingRun === run.id}
                              title="Datei prüfen (Schema und Bankregeln)"
                              data-testid={`sepa-check-${run.run_number}`}
                            >
                              <ShieldCheck className="w-4 h-4 text-blue-600" />
                            </Button>

                            {run.status === 'draft' && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => exportRun(run.id)}
                                  title="XML exportieren"
                                >
                                  <FileText className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                            
                            {run.status === 'exported' && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => downloadRun(run.id, run.xml_filename)}
                                  title="XML herunterladen"
                                >
                                  <Download className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setFintsTarget({ type: 'run', id: run.id })}
                                  title="An die Bank senden"
                                  data-testid={`sepa-fints-${run.run_number}`}
                                >
                                  <Landmark className="w-4 h-4 text-emerald-600" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => markRunStatus(run.id, 'mark-submitted')}
                                  title="Als eingereicht markieren"
                                >
                                  <Play className="w-4 h-4 text-blue-500" />
                                </Button>
                              </>
                            )}
                            
                            {!['submitted', 'completed'].includes(run.status) && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => deleteRun(run)}
                                title="Löschen"
                                data-testid={`sepa-delete-${run.run_number}`}
                              >
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            )}

                            {run.status === 'submitted' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => markRunStatus(run.id, 'mark-completed')}
                                title="Als abgeschlossen markieren"
                              >
                                <CheckCircle2 className="w-4 h-4 text-green-500" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="compare" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Lastschriftläufe vergleichen</CardTitle>
              <CardDescription>
                Vergleichen Sie zwei Läufe, um Änderungen zu erkennen
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-4 items-end">
                <div className="flex-1">
                  <Label>Lauf 1</Label>
                  <select
                    className="w-full h-10 px-3 border rounded-md"
                    value={compareRuns.run1}
                    onChange={(e) => setCompareRuns({...compareRuns, run1: e.target.value})}
                  >
                    <option value="">Wählen...</option>
                    {runs.map(r => (
                      <option key={r.id} value={r.run_number}>
                        #{r.run_number} - {formatDate(r.billing_month)} ({formatCurrency(r.total_amount)})
                      </option>
                    ))}
                  </select>
                </div>
                <ArrowLeftRight className="w-6 h-6 text-gray-400" />
                <div className="flex-1">
                  <Label>Lauf 2</Label>
                  <select
                    className="w-full h-10 px-3 border rounded-md"
                    value={compareRuns.run2}
                    onChange={(e) => setCompareRuns({...compareRuns, run2: e.target.value})}
                  >
                    <option value="">Wählen...</option>
                    {runs.map(r => (
                      <option key={r.id} value={r.run_number}>
                        #{r.run_number} - {formatDate(r.billing_month)} ({formatCurrency(r.total_amount)})
                      </option>
                    ))}
                  </select>
                </div>
                <Button onClick={compareSepaRuns}>
                  Vergleichen
                </Button>
              </div>

              {comparison && (
                <div className="mt-6 space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    <Card>
                      <CardContent className="p-4 text-center">
                        <p className="text-sm text-gray-500">Betragsdifferenz</p>
                        <p className={`text-2xl font-bold ${comparison.amount_difference >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {comparison.amount_difference >= 0 ? '+' : ''}{formatCurrency(comparison.amount_difference)}
                        </p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4 text-center">
                        <p className="text-sm text-gray-500">Neue Zahlungspflichtige</p>
                        <p className="text-2xl font-bold text-blue-600">{comparison.new_debtors.length}</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4 text-center">
                        <p className="text-sm text-gray-500">Entfernte Zahlungspflichtige</p>
                        <p className="text-2xl font-bold text-orange-600">{comparison.removed_debtors.length}</p>
                      </CardContent>
                    </Card>
                  </div>

                  {comparison.changed_amounts.length > 0 && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Geänderte Beträge</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Name</TableHead>
                              <TableHead>Lauf #{comparison.run1_number}</TableHead>
                              <TableHead>Lauf #{comparison.run2_number}</TableHead>
                              <TableHead>Differenz</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {comparison.changed_amounts.map((c, i) => (
                              <TableRow key={`${c.debtor_name}-${i}`}>
                                <TableCell>{c.debtor_name}</TableCell>
                                <TableCell>{formatCurrency(c.run1_amount)}</TableCell>
                                <TableCell>{formatCurrency(c.run2_amount)}</TableCell>
                                <TableCell className={c.difference >= 0 ? 'text-green-600' : 'text-red-600'}>
                                  {c.difference >= 0 ? '+' : ''}{formatCurrency(c.difference)}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="imikel" className="mt-4">
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-3 bg-brand-red/5 border border-brand-red/20 rounded-md">
              <span className="text-sm font-medium text-brand-red">Mandant für Import:</span>
              <select
                value={imikelTenant}
                onChange={(e) => setImikelTenant(e.target.value)}
                className="px-3 py-1.5 rounded border border-brand-red/30 bg-white text-sm text-slate-900"
                data-testid="imikel-tenant-select"
              >
                <option value="MA-GRE">Musik Akademie Greven (TA.)</option>
                <option value="MA-BN">Musik Akademie Bonn (MAH.)</option>
              </select>
            </div>
            <ImikelImport
              key={imikelTenant}
              tenantSlug={imikelTenant}
              tenantLabel={imikelTenant === 'MA-BN' ? 'Musik Akademie Bonn' : 'Musik Akademie Greven'}
              allowedPrefixesText={imikelTenant === 'MA-BN' ? 'MAH.' : 'TA.'}
            />
          </div>
        </TabsContent>
      </Tabs>

      {/* Creditor Config Dialog */}
      <Dialog open={showConfigDialog} onOpenChange={setShowConfigDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Gläubiger-Konfiguration</DialogTitle>
            <DialogDescription>
              Je ein Konto für den Einzug zum 01. und zum 15.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2">
            {[1, 15].map((tag) => (
              <Button
                key={tag}
                type="button"
                size="sm"
                variant={configTag === tag ? 'default' : 'outline'}
                data-testid={`config-debit-${tag}`}
                onClick={() => {
                  setConfigTag(tag);
                  const eigen = konten.find((k) => k.debit_day === tag);
                  const vorlage = kontoFuerEinzugstag(konten, 1);
                  setConfigForm(formularAus(eigen, vorlage));
                }}
              >
                zum {String(tag).padStart(2, '0')}.
              </Button>
            ))}
          </div>
          <div className="space-y-4">
            <div>
              <Label>Name (Gläubiger)</Label>
              <Input
                value={configForm.name}
                onChange={(e) => setConfigForm({...configForm, name: e.target.value})}
                placeholder="Musikschule XYZ"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>IBAN</Label>
                <Input
                  value={configForm.iban}
                  onChange={(e) => {
                    const iban = e.target.value.toUpperCase();
                    setConfigForm({ ...configForm, iban });
                    const cleaned = normalizeIban(iban);
                    if (cleaned.length < 15) return;
                    lookupIbanInfo(cleaned).then((info) => {
                      if (!info?.bic) return;
                      setConfigForm((prev) => (
                        normalizeIban(prev.iban) === cleaned
                          ? { ...prev, bic: info.bic }
                          : prev
                      ));
                    });
                  }}
                  placeholder="DE89..."
                />
              </div>
              <div>
                <Label>BIC</Label>
                <Input
                  value={configForm.bic}
                  onChange={(e) => setConfigForm({...configForm, bic: e.target.value})}
                  placeholder="aus IBAN"
                />
              </div>
            </div>
            {/*
              Im Platzhalter stand die Beispielnummer aus der SEPA-Dokumentation.
              Eine aktive Konfiguration trug sie am Ende wirklich — mit ihr weist
              die Bank jede Datei ab. Jetzt nur die Form, nichts Abtippbares.
            */}
            <div>
              <Label>Gläubiger-Identifikationsnummer</Label>
              <Input
                value={configForm.creditor_id}
                onChange={(e) => setConfigForm({...configForm, creditor_id: e.target.value})}
                placeholder="DE..ZZZ........... (18 Stellen)"
                data-testid="config-creditor-id"
              />
              <p className="text-xs text-gray-500 mt-1">
                Die Gläubiger-ID steht im Bescheid der Deutschen Bundesbank. Die Nummer aus
                Beispielen und Anleitungen ist bei keiner Bank registriert.
              </p>
            </div>
            <div>
              <Label>Adresse</Label>
              <Input
                value={configForm.address}
                onChange={(e) => setConfigForm({...configForm, address: e.target.value})}
                placeholder="Musterstraße 1"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>PLZ</Label>
                <Input
                  value={configForm.postal_code}
                  onChange={(e) => setConfigForm({...configForm, postal_code: e.target.value})}
                  placeholder="12345"
                />
              </div>
              <div>
                <Label>Ort</Label>
                <Input
                  value={configForm.city}
                  onChange={(e) => setConfigForm({...configForm, city: e.target.value})}
                  placeholder="Musterstadt"
                />
                <PlzOrtBridge
                  postalCode={configForm.postal_code}
                  city={configForm.city}
                  onCity={(value) => setConfigForm((prev) => ({ ...prev, city: value }))}
                  testId="sepa-config-plz"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfigDialog(false)}>
              Abbrechen
            </Button>
            <Button onClick={saveCreditorConfig} data-testid="config-save">
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview/Create Run Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent
          className="top-16 z-[80] flex max-h-[calc(100dvh-7.5rem)] w-full max-w-3xl translate-y-0 flex-col gap-0 overflow-hidden p-0 pointer-events-auto"
          data-testid="sepa-new-run-dialog"
        >
          <DialogHeader className="shrink-0 px-6 pt-6 pr-12">
            <DialogTitle>Neuer Lastschriftlauf</DialogTitle>
            <DialogDescription>
              Erstellen Sie einen neuen SEPA-Lastschriftlauf
            </DialogDescription>
          </DialogHeader>

          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-4" data-testid="sepa-new-run-scroll">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="run-execution-date">Ausführungsdatum</Label>
                <DateInput
                  id="run-execution-date"
                  data-testid="run-execution-date"
                  value={previewForm.execution_date}
                  onChange={(e) => setPreviewForm({
                    ...previewForm,
                    execution_date: e.target.value,
                    billing_month: monatZumTermin(e.target.value),
                    execution_mode: previewForm.execution_mode === 'scheduled' ? 'scheduled' : 'custom',
                  })}
                />
                <div className="mt-2 flex flex-wrap gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant={previewForm.execution_mode === 'scheduled' ? 'default' : 'outline'}
                    data-testid="run-termin-einzugstag"
                    onClick={() => setzeAusfuehrung('scheduled')}
                  >
                    Zum Einzugstag
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={previewForm.execution_mode === 'immediate' ? 'default' : 'outline'}
                    data-testid="run-termin-sofort"
                    onClick={() => setzeAusfuehrung('immediate')}
                  >
                    Sofort
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={previewForm.execution_mode === 'custom' ? 'default' : 'outline'}
                    data-testid="run-termin-eigen"
                    onClick={() => setzeAusfuehrung('custom')}
                  >
                    Eigenes Datum
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Datum, an dem die Bank die Lastschriften ausführt
                </p>
                {executionDayMismatch && (
                  <div className="mt-1 flex items-start gap-1.5 rounded-md bg-amber-50 border border-amber-200 px-2 py-1.5" data-testid="execution-date-mismatch-warning">
                    <AlertTriangle className="w-3.5 h-3.5 text-brand-warning mt-0.5 shrink-0" />
                    <p className="text-xs text-amber-700">
                      Das Datum fällt nicht auf den {String(previewForm.debit_day).padStart(2, '0')}. des Monats.{' '}
                      <button
                        type="button"
                        className="underline font-medium"
                        data-testid="apply-suggested-execution-date-btn"
                        onClick={() => {
                          const execution_date = suggestExecutionDate(previewForm.debit_day);
                          setPreviewForm({
                            ...previewForm,
                            execution_date,
                            billing_month: monatZumTermin(execution_date),
                            execution_mode: 'scheduled',
                          });
                        }}
                      >
                        Vorschlag übernehmen: {formatDate(suggestExecutionDate(previewForm.debit_day))}
                      </button>
                    </p>
                  </div>
                )}
              </div>
              <div>
                <Label>Abrechnungsmonat</Label>
                <p className="text-sm mt-1" data-testid="run-billing-month">
                  {monatsname(previewForm.billing_month)}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Immer der Monat des Ausführungsdatums. Mai, Juni oder Juli lassen sich auch heute noch anlegen.
                </p>
              </div>
            </div>

            <div>
              <Label>Abbuchungstermin</Label>
              <select
                className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                value={previewForm.debit_day ?? ''}
                onChange={(e) => handleDebitDayChange(e.target.value)}
                data-testid="run-debit-day-select"
              >
                <option value="1">Nur Zahler zum 01.</option>
                <option value="15">Nur Zahler zum 15.</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Filtert nach dem beim Zahler hinterlegten Abbuchungstermin (iMikel: 01./15. des Monats). Das Ausführungsdatum wird automatisch auf den nächsten passenden Termin gesetzt.
              </p>
            </div>
            
            <div className="rounded-lg border-2 border-blue-200 bg-blue-50 p-3 text-center" data-testid="sepa-run-schedule-summary">
              <p className="font-semibold text-blue-900">
                Einzugstag {String(previewForm.debit_day).padStart(2, '0')}. · {
                  previewForm.execution_mode === 'immediate'
                    ? `Ausführung sofort am ${formatDate(previewForm.execution_date)}`
                    : `Ausführung ${formatDate(previewForm.execution_date)}`
                }
              </p>
              <p className="text-xs text-blue-700">Dieser Lauf enthält ausschließlich Zahler dieses Einzugstags.</p>
              <p className="text-xs text-blue-700" data-testid="sepa-run-konto">
                {(() => {
                  const konto = kontoFuerEinzugstag(konten, previewForm.debit_day);
                  return konto
                    ? `Einzug über ${konto.bank_label ? `${konto.bank_label} · ` : ''}${konto.iban}`
                    : `Für den ${String(previewForm.debit_day).padStart(2, '0')}. ist noch kein Konto hinterlegt.`;
                })()}
              </p>
            </div>

            <Button
              onClick={loadPreview}
              disabled={executionDayMismatch || previewLoading}
              variant="outline"
              className="w-full"
              data-testid="sepa-preview-load"
            >
              {previewLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />}
              {previewLoading ? 'Vorschau wird geladen' : 'Vorschau laden'}
            </Button>

            {preview && (
              <div className="space-y-4 border-t pt-4">
                <div className="grid grid-cols-3 gap-4">
                  <Card>
                    <CardContent className="p-4 text-center">
                      <p className="text-sm text-gray-500">Rechnungen</p>
                      <p className="text-2xl font-bold">{preview.invoice_count}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4 text-center">
                      <p className="text-sm text-gray-500">Gesamtbetrag</p>
                      <p className="text-2xl font-bold text-green-600">
                        {formatCurrency(preview.total_amount)}
                      </p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4 text-center">
                      <p className="text-sm text-gray-500">Warnungen</p>
                      <p className={`text-2xl font-bold ${preview.warnings.length > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                        {preview.warnings.length}
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {preview.warnings.length > 0 && (
                  <Card className="bg-orange-50 border-orange-200">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg text-orange-700">
                        <AlertCircle className="w-5 h-5 inline mr-2" />
                        Warnungen ({preview.warnings.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="max-h-40 overflow-y-auto text-sm">
                        {preview.warnings.slice(0, 10).map((w, i) => (
                          <p key={i} className="text-orange-700">{w}</p>
                        ))}
                        {preview.warnings.length > 10 && (
                          <p className="text-orange-600 font-medium mt-2">
                            ... und {preview.warnings.length - 10} weitere Warnungen
                          </p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {preview.invoices.length > 0 && (
                  <div className="max-h-60 overflow-y-auto border rounded-md">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Rechnung</TableHead>
                          <TableHead>Zahlungspflichtiger</TableHead>
                          <TableHead>IBAN</TableHead>
                          <TableHead>Betrag</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {preview.invoices.slice(0, 20).map((inv, i) => (
                          <TableRow key={inv.invoice_number || i}>
                            <TableCell className="font-mono text-sm">{inv.invoice_number}</TableCell>
                            <TableCell>{inv.payer_name}</TableCell>
                            <TableCell className="font-mono text-sm">{inv.iban || '-'}</TableCell>
                            <TableCell>{formatCurrency(inv.amount)}</TableCell>
                            <TableCell>
                              {inv.has_mandate ? (
                                <CheckCircle2 className="w-4 h-4 text-green-500" />
                              ) : (
                                <AlertCircle className="w-4 h-4 text-orange-500" />
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter className="shrink-0 border-t bg-background px-6 py-4">
            <Button variant="outline" onClick={() => setShowPreviewDialog(false)}>
              Abbrechen
            </Button>
            <Button
              onClick={createRun}
              disabled={!preview || preview.total_amount === 0 || executionDayMismatch}
            >
              <Plus className="w-4 h-4 mr-2" />
              Lastschriftlauf erstellen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Run Detail Dialog */}
      <Dialog open={showRunDetailDialog} onOpenChange={setShowRunDetailDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          {selectedRun && (
            <>
              <DialogHeader>
                <DialogTitle>
                  Lastschriftlauf #{selectedRun.run_number}
                  <span className="ml-2">{getStatusBadge(selectedRun.status)}</span>
                </DialogTitle>
                <DialogDescription>
                  Einzugstag: {String(selectedRun.debit_day).padStart(2, '0')}. |
                  Ausführung: {formatDate(selectedRun.execution_date)} |
                  Monat: {formatDate(selectedRun.billing_month)}
                </DialogDescription>
              </DialogHeader>

              {(selectedRun.status === 'draft' || selectedRun.status === 'exported') && (
                <div className="max-w-xs space-y-2">
                  <Label htmlFor="sepa-run-execution-date">Ausführungsdatum</Label>
                  <DateInput
                    id="sepa-run-execution-date"
                    value={(selectedRun.execution_date || '').slice(0, 10)}
                    onChange={(e) => speichereTermin(selectedRun.id, e.target.value)}
                    data-testid="sepa-run-execution-date"
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    data-testid="sepa-run-sofort"
                    onClick={() => speichereTermin(
                      selectedRun.id,
                      fruehesterBankarbeitstag(),
                      'immediate',
                    )}
                  >
                    Sofort
                  </Button>
                </div>
              )}

              <div className="grid grid-cols-4 gap-4 my-4">
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-sm text-gray-500">Transaktionen</p>
                    <p className="text-xl font-bold">{selectedRun.transaction_count}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-sm text-gray-500">Gesamtbetrag</p>
                    <p className="text-xl font-bold text-green-600">
                      {formatCurrency(selectedRun.total_amount)}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-sm text-gray-500">Gläubiger</p>
                    <p className="text-sm font-medium truncate">{selectedRun.creditor_name}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-sm text-gray-500">XML-Datei</p>
                    <p className="text-sm font-medium truncate">
                      {selectedRun.xml_filename || '-'}
                    </p>
                  </CardContent>
                </Card>
              </div>

              <div className="border rounded-md max-h-96 overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-8"></TableHead>
                      <TableHead>Rechnung</TableHead>
                      <TableHead>Zahlungspflichtiger</TableHead>
                      <TableHead>IBAN</TableHead>
                      <TableHead>Mandat</TableHead>
                      <TableHead>Typ</TableHead>
                      <TableHead>Betrag</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedRun.transactions?.map((txn) => (
                      <TableRow key={txn.id}>
                        <TableCell>
                          {selectedRun.status === 'exported' && txn.status !== 'submitted' ? (
                            <input
                              type="checkbox"
                              checked={gewaehltePosten.includes(txn.id)}
                              onChange={() => togglePosten(txn.id)}
                              aria-label={`Lastschrift ${txn.debtor_name || ''} auswählen`}
                              data-testid={`sepa-txn-${txn.id}`}
                            />
                          ) : txn.status === 'submitted' ? (
                            <span className="text-xs text-slate-400">gesendet</span>
                          ) : null}
                        </TableCell>
                        <TableCell className="font-mono text-sm">
                          {txn.invoice_number || '-'}
                        </TableCell>
                        <TableCell>{txn.debtor_name}</TableCell>
                        <TableCell className="font-mono text-sm">
                          {txn.debtor_iban.slice(0, 4)}****{txn.debtor_iban.slice(-4)}
                        </TableCell>
                        <TableCell className="font-mono text-sm">
                          {txn.mandate_reference.slice(0, 10)}...
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{txn.sequence_type}</Badge>
                        </TableCell>
                        <TableCell className="font-medium">
                          {formatCurrency(txn.amount)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {selectedRun.status === 'exported' && gewaehltePosten.length > 0 && (
                <div className="flex justify-end">
                  <Button
                    onClick={() => setFintsTarget({
                      type: 'run',
                      id: selectedRun.id,
                      lineIds: gewaehltePosten,
                    })}
                    data-testid="sepa-fints-auswahl"
                  >
                    <Landmark className="w-4 h-4 mr-2" />
                    Ausgewählte an die Bank
                  </Button>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Prüfung der Datei */}
      <Dialog open={showCheckDialog} onOpenChange={setShowCheckDialog}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="sepa-check-dialog">
          <DialogHeader>
            <DialogTitle>
              Prüfung {checkResult ? `Lastschriftlauf #${checkResult.lauf_nummer}` : ''}
            </DialogTitle>
            <DialogDescription>
              Geprüft wird die Struktur gegen das Bankschema und der Inhalt gegen die Regeln,
              an denen Einreichungen scheitern. Es wird nichts exportiert und nichts verändert.
            </DialogDescription>
          </DialogHeader>

          {!checkResult ? (
            <div className="py-8 text-center text-gray-500" data-testid="sepa-check-loading">
              <RefreshCw className="w-5 h-5 mx-auto mb-2 animate-spin" />
              Datei wird geprüft...
            </div>
          ) : (
            <div className="space-y-4">
              <div
                className={`p-4 rounded-lg border ${
                  checkResult.einreichbar
                    ? 'bg-green-50 border-green-200'
                    : 'bg-red-50 border-red-200'
                }`}
                data-testid="sepa-check-summary"
              >
                <div className="flex items-start gap-3">
                  {checkResult.einreichbar ? (
                    <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 shrink-0" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
                  )}
                  <div>
                    <p className="font-medium">
                      {checkResult.einreichbar
                        ? 'Keine Fehler gefunden'
                        : `${checkResult.fehler} ${checkResult.fehler === 1 ? 'Fehler' : 'Fehler'} gefunden`}
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      {checkResult.posten_geprueft} Posten geprüft
                      {checkResult.warnungen > 0 && `, ${checkResult.warnungen} Hinweise`}
                      {checkResult.schema_geprueft
                        ? `, Schema ${checkResult.format_version}${
                            checkResult.schema_quelle ? ` (${checkResult.schema_quelle})` : ''
                          } geprüft`
                        : ', Schemaprüfung nicht verfügbar'}
                      .
                    </p>
                    {!checkResult.einreichbar && (
                      <p className="text-sm text-red-700 mt-2">
                        Fehler führen bei der Bank zur Abweisung. Bitte vor dem Einreichen beheben.
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {checkResult.befunde.length === 0 ? (
                <p className="text-sm text-gray-600">
                  Auch keine Hinweise — die Datei ist aus Sicht der Prüfung in Ordnung.
                </p>
              ) : (
                <div className="space-y-3">
                  {checkResult.befunde.map((b) => {
                    const viele = (b.anzahl || 0) > 5;
                    const offen = !!befundeOffen[b.regel];
                    const zeigeListe = !viele || offen;
                    const oeffnetKorrektur = !viele && !!b.ziel?.art;
                    return (
                    <div
                      key={b.regel}
                      className={`p-3 rounded-lg border ${
                        b.stufe === 'fehler'
                          ? 'bg-red-50 border-red-200'
                          : 'bg-amber-50 border-amber-200'
                      } ${(viele || oeffnetKorrektur) ? 'cursor-pointer' : ''}`}
                      data-testid={`sepa-befund-${b.regel}`}
                      onClick={() => {
                        if (viele) {
                          setBefundeOffen((stand) => ({ ...stand, [b.regel]: !stand[b.regel] }));
                          return;
                        }
                        if (b.ziel?.art) oeffneBefund(b.ziel);
                      }}
                    >
                      <div className="flex items-start gap-2">
                        {b.stufe === 'fehler' ? (
                          <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
                        ) : (
                          <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                        )}
                        <div className="min-w-0">
                          <div className="font-medium text-sm flex items-center gap-2 flex-wrap">
                            {b.ziel?.art ? (
                              <button
                                type="button"
                                className="underline text-left"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  oeffneBefund(b.ziel);
                                }}
                                data-testid={`sepa-befund-ziel-${b.regel}`}
                              >
                                {b.titel}
                              </button>
                            ) : (
                              <span>{b.titel}</span>
                            )}
                            {b.anzahl > 1 && (
                              <Badge variant="outline">{b.anzahl}&times;</Badge>
                            )}
                          </div>
                          <p className="text-sm text-gray-700 mt-0.5">{b.erlaeuterung}</p>
                          {viele && !offen && (
                            <p className="text-xs text-gray-600 mt-1 underline">
                              Klicken, um alle {b.anzahl} anzuzeigen und zu korrigieren
                            </p>
                          )}
                          {oeffnetKorrektur && (
                            <p className="text-xs text-gray-600 mt-1 underline">
                              Klicken, um zu korrigieren
                            </p>
                          )}
                          {zeigeListe && ((b.ziele || []).some((z) => z?.art && z?.id) || b.beispiele.length > 0) && (
                            <p className="text-xs text-gray-500 mt-1 break-words max-h-48 overflow-y-auto">
                              Betroffen:{' '}
                              {(b.ziele || []).filter((z) => z?.art && z?.id).map((z, idx, alle) => (
                                <React.Fragment key={`${z.id}-${idx}`}>
                                  {idx > 0 && ' · '}
                                  <a
                                    href={`/payer-accounts?guardian=${encodeURIComponent(z.id)}`}
                                    className="underline"
                                    data-testid={`sepa-befund-link-${b.regel}-${idx}`}
                                    onClick={(event) => event.stopPropagation()}
                                  >
                                    {z.text}
                                  </a>
                                  {idx === alle.length - 1 && b.beispiele.some((t) => !alle.some((x) => x.text === t)) && ' · '}
                                </React.Fragment>
                              ))}
                              {b.beispiele
                                .filter((t) => !(b.ziele || []).some((z) => z?.text === t && z?.id))
                                .join(' · ')}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCheckDialog(false)}>
              Schließen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <FintsSubmitDialog
        open={!!fintsTarget}
        onOpenChange={(weiter) => { if (!weiter) setFintsTarget(null); }}
        targetType={fintsTarget?.type}
        targetId={fintsTarget?.id}
        lineIds={fintsTarget?.lineIds}
        onSubmitted={fetchData}
      />
      <FintsConnectionsDialog open={showBanks} onOpenChange={setShowBanks} />
    </div>
    </Layout>
  );
};

export default SepaPage;
